import React, { useEffect, useState } from "react";
import { api, formatGHS, formatApiErrorDetail } from "@/lib/api";
import { Loader2, UserPlus, Users, Crown, RefreshCw, TrendingUp, Mail, Phone } from "lucide-react";

const STAGES = ["all", "subscriber", "lead", "customer", "repeat_customer"];

const stageLabel = {
  subscriber: "Subscriber",
  lead: "Lead",
  customer: "Customer",
  repeat_customer: "Repeat customer",
};

const stageClass = {
  subscriber: "bg-blue-100 text-blue-800",
  lead: "bg-yellow-100 text-yellow-800",
  customer: "bg-green-100 text-green-800",
  repeat_customer: "bg-purple-100 text-purple-800",
};

const sourceLabel = {
  newsletter: "Newsletter",
  contact: "Contact form",
  quote: "Custom quote",
  booking: "Booking",
  wingman: "Wingman chat",
  manual: "Manual",
};

export default function AdminLeadsPage() {
  const [leads, setLeads] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: l }, { data: f }] = await Promise.all([
        api.get(`/admin/leads${filter !== "all" ? `?stage=${filter}` : ""}`),
        api.get("/admin/leads/funnel"),
      ]);
      setLeads(l);
      setFunnel(f);
    } catch (e) {
      // load failed — show empty state instead of crashing the admin panel
      console.error("Failed to load leads/funnel", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  return (
    <div className="p-8" data-testid="admin-leads-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <span className="eyebrow">Marketing Funnel</span>
          <h1 className="font-display text-3xl font-bold text-[#0d2040] mt-2">Leads & Customers</h1>
        </div>
        <button onClick={load} className="btn-outline-navy" data-testid="leads-refresh">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Funnel cards */}
      {funnel && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8" data-testid="funnel-cards">
          <FunnelCard label="Subscribers" value={funnel.subscribers} icon={UserPlus} accent="bg-blue-500" />
          <FunnelCard label="Leads" value={funnel.leads} icon={Users} accent="bg-yellow-500" />
          <FunnelCard label="Customers" value={funnel.customers} icon={Crown} accent="bg-green-600" />
          <FunnelCard label="Repeat" value={funnel.repeat_customers} icon={RefreshCw} accent="bg-purple-600" />
          <FunnelCard
            label="Conversion rate"
            value={`${funnel.conversion_rate_pct}%`}
            icon={TrendingUp}
            accent="bg-[#e63946]"
            sublabel={`${funnel.paying_customers} of ${funnel.total} converted`}
          />
        </div>
      )}

      {/* By source */}
      {funnel && Object.keys(funnel.by_source || {}).length > 0 && (
        <div className="bg-white border border-gray-200 p-5 mb-8" data-testid="funnel-by-source">
          <h3 className="font-display text-lg font-semibold text-[#0d2040] mb-3">Where leads come from</h3>
          <div className="flex flex-wrap gap-3">
            {Object.entries(funnel.by_source).map(([src, count]) => (
              <div key={src} className="flex items-center gap-2 px-3 py-2 bg-[#f7f8fa] border border-gray-200">
                <span className="text-xs uppercase tracking-widest text-gray-500">{sourceLabel[src] || src}</span>
                <span className="font-display font-bold text-[#e63946]">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-3 mb-4">
        <span className="text-xs uppercase tracking-widest text-gray-500">Filter by stage</span>
        <select className="titan-input max-w-xs" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="leads-stage-filter">
          {STAGES.map((s) => <option key={s} value={s}>{s === "all" ? "All stages" : stageLabel[s]}</option>)}
        </select>
      </div>

      {/* Leads table */}
      {loading ? (
        <div className="py-16 text-center"><Loader2 className="inline-block animate-spin text-[#e63946]" /></div>
      ) : leads.length === 0 ? (
        <div className="bg-white border border-gray-200 p-10 text-center text-gray-500">No leads yet.</div>
      ) : (
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-leads-table">
            <thead className="bg-[#0d2040] text-white">
              <tr>
                <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">Contact</th>
                <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">Stage</th>
                <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">Sources</th>
                <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">Bookings</th>
                <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">Spent</th>
                <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">First seen</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-t border-gray-100 hover:bg-[#f7f8fa]">
                  <td className="p-3">
                    <div className="font-medium text-[#0d2040]">{l.name || "—"}</div>
                    <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5"><Mail size={11} /> {l.email}</div>
                    {l.phone && <div className="text-xs text-gray-500 flex items-center gap-1"><Phone size={11} /> {l.phone}</div>}
                  </td>
                  <td className="p-3">
                    <span className={`text-xs font-bold uppercase tracking-wider px-2 py-1 ${stageClass[l.stage] || "bg-gray-100"}`}>
                      {stageLabel[l.stage] || l.stage}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1">
                      {(l.sources || [l.source]).map((s) => (
                        <span key={s} className="text-[0.65rem] uppercase tracking-wider px-2 py-0.5 bg-gray-100 text-gray-700">
                          {sourceLabel[s] || s}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="p-3 font-medium text-[#0d2040]">{l.total_bookings || 0}</td>
                  <td className="p-3 font-display font-bold text-[#e63946]">{l.total_spent ? formatGHS(l.total_spent) : "—"}</td>
                  <td className="p-3 text-xs text-gray-500">{(l.first_seen_at || "").slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function FunnelCard({ label, value, icon: Icon, accent, sublabel }) {
  return (
    <div className="bg-white border border-gray-200 p-5" data-testid={`funnel-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-gray-500">{label}</div>
        <div className={`w-8 h-8 ${accent} flex items-center justify-center`}>
          <Icon size={14} className="text-white" />
        </div>
      </div>
      <div className="font-display text-3xl font-bold text-[#0d2040] mt-3">{value}</div>
      {sublabel && <div className="text-[0.7rem] text-gray-500 mt-1">{sublabel}</div>}
    </div>
  );
}
